import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';

import { OFFLINE_DELETE_COPY_CONFIRMATION } from '../../offline-mutation-outbox/services/offline-mutation-error';
import { OfflineMutationOutboxService } from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type { OfflineMutationRecord } from '../../offline-mutation-outbox/types';
import * as styles from './workspace-overview.css';

export function DeletionPendingLine({
  phase,
  permanent = false,
  cancel,
  retry,
  testId = 'deletion-pending',
}: {
  phase: 'queued' | 'confirming' | 'unconfirmed';
  permanent?: boolean;
  cancel: () => Promise<unknown>;
  retry: () => Promise<unknown>;
  testId?: string;
}) {
  const t = useI18n();
  const action =
    phase === 'queued'
      ? {
          label: t['com.streamlain.dataview.database.deletionPending.cancel'](),
          run: cancel,
          suffix: 'cancel',
        }
      : phase === 'unconfirmed'
        ? {
            label:
              t['com.streamlain.dataview.database.deletionPending.retry'](),
            run: retry,
            suffix: 'retry',
          }
        : null;
  return (
    <div className={styles.itemMeta} data-phase={phase} data-testid={testId}>
      <span className={styles.metaSegment}>
        {permanent && phase === 'queued'
          ? t[
              'com.streamlain.dataview.database.deletionPending.permanentQueued'
            ]()
          : t[`com.streamlain.dataview.database.deletionPending.${phase}`]()}
      </span>
      {action && (
        <button
          className={styles.itemActionNeutral}
          data-testid={`${testId}-${action.suffix}`}
          onClick={event => {
            event.stopPropagation();
            event.preventDefault();
            action.run().catch(console.error);
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** The same phase and actions for document and App container requests. */
export function OutboxDeletionPendingLine({
  kind,
  id,
}: {
  kind: 'doc' | 'appBase' | 'folder';
  id: string;
}) {
  const outbox = useService(OfflineMutationOutboxService);
  const pending = useLiveData(outbox.pendingMutations$);
  const failed = useLiveData(outbox.failedMutations$);
  const matches = (record: OfflineMutationRecord) => {
    if (
      record.operation === 'folder.trash' ||
      record.operation === 'folder.restore' ||
      record.operation === 'folder.permanentDelete'
    ) {
      const payload = record.payload as {
        folderId: string;
        affectedDocIds?: string[];
      };
      return kind === 'folder'
        ? payload.folderId === id
        : kind === 'doc' && !!payload.affectedDocIds?.includes(id);
    }

    if (
      kind === 'doc' &&
      (record.operation === 'doc.trash' ||
        record.operation === 'doc.restore' ||
        record.operation === 'doc.permanentDelete')
    ) {
      return (record.payload as { docId: string }).docId === id;
    }
    if (
      kind === 'appBase' &&
      (record.operation === 'appBase.trash' ||
        record.operation === 'appBase.restore' ||
        record.operation === 'appBase.permanentDelete')
    ) {
      return (record.payload as { appBaseId: string }).appBaseId === id;
    }
    return false;
  };
  const record = [
    ...failed.filter(
      record =>
        record.failure === 'retries-exhausted' ||
        record.lastError === OFFLINE_DELETE_COPY_CONFIRMATION
    ),
    ...pending,
  ]
    .filter(matches)
    .at(-1);
  if (!record) return null;
  const phase =
    record.status === 'pending'
      ? 'queued'
      : record.status === 'flushing'
        ? 'confirming'
        : 'unconfirmed';
  return (
    <DeletionPendingLine
      permanent={record.operation.endsWith('.permanentDelete')}
      phase={phase}
      cancel={() => outbox.cancelPending(record.id)}
      retry={() => outbox.retryFailed(record.id)}
    />
  );
}
